import React from 'react'
import { useLocation, useParams } from 'react-router'
import dayjs from 'dayjs'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { Spinner } from '@mobi/component-library/Legacy/Spinner'
import { Button } from '@mobi/component-library/Base/Buttons'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors } from '@mobi/component-library/Foundations'
import { NextRaceDrawer } from '@core/Components/BettingDrawer/NextRaceDrawer'
import { MainContainer } from '@core/Components/Containers'
import { NoticeBox } from '@core/Components/NoticeBox'
import {
  getToteRaceTopic,
  ToteRaceAbandonedPushEvent,
  ToteRaceStatusChangedPushEvent,
} from '@core/State/PushData'
import { iotSubscribeTopics, iotUnsubscribeTopics } from '@mobi/utils/awsiot'
import { useLogon, useObservableImmutable } from '@core/Utils/hooks'
import { fetchCampaignsAsync } from '@core/State/UserAccount/async-signals'
import { SetGoToMeeting, SetGoToNextRace, SetIsRaceClosed, SetMysteryData, state$ } from './driver'
import { MysteryBetTabs } from './Components/MysteryBetTabs'
import { MeetingInformation } from './Components/MeetingInformation'
import { useMysteryBetDetails } from '@core/Areas/MysteryBet/Hooks/useMysteryBetDetails'
import { appHistory } from '@core/Utils/routing/history'

const NEXT_RACE_PATH = '/tote/mystery'

export const MysteryBetPage = (): JSX.Element | null => {
  const location = useLocation()
  const { meetingId, raceNumber } = useParams<{ meetingId: string; raceNumber: string }>()
  const date = new URLSearchParams(location.search).get('date') || undefined
  const { isRaceClosed, isBetConfirmed } = useObservableImmutable(state$, [
    'isRaceClosed',
    'isBetConfirmed',
  ])
  const { isLoggedIn } = useLogon()

  const isForSpecificRace = !!meetingId && !!raceNumber && !!date

  const { isError, data, isFetching, refetch } = useMysteryBetDetails({
    meetingId,
    date,
    raceNumber: raceNumber ? +raceNumber : undefined,
  })

  React.useEffect(() => {
    SetMysteryData(data)
  }, [data])

  React.useEffect(() => {
    if (isLoggedIn) {
      fetchCampaignsAsync()
    }
  }, [isLoggedIn])

  React.useEffect(() => {
    SetIsRaceClosed(data?.Race != null && data.Race.RaceStatusText !== 'Open')
    const raceKey = data?.RaceKeyForLiveUpdates
    let subscription: Rx.IDisposable
    if (raceKey) {
      subscription = iotSubscribeTopics(getTopics(raceKey)).subscribe(e => {
        if (e.topic?.includes(raceKey)) {
          if (e.payload?.eventType === 'ToteRaceStatusChanged') {
            const statusChangedPayload = e.payload as ToteRaceStatusChangedPushEvent
            SetIsRaceClosed(statusChangedPayload.status !== 'open')
          } else if (e.payload?.eventType === 'ToteRaceAbandonChanged') {
            const abandonChangedPayload = e.payload as ToteRaceAbandonedPushEvent
            if (abandonChangedPayload.status !== 'unabandoned') {
              SetIsRaceClosed(true)
            }
          }
        }
      })
    }

    return function cleanUp(): void {
      subscription?.dispose()
      if (raceKey) {
        iotUnsubscribeTopics(getTopics(raceKey))
      }
    }
  }, [data])

  const formattedDate = dayjs(data?.Meeting?.MeetingDate).format('YYYY-MM-DD')
  const goToNextRace = React.useCallback(
    () => (location.pathname === NEXT_RACE_PATH ? refetch() : appHistory.push(NEXT_RACE_PATH)),
    [location.pathname, refetch]
  )
  const goToMeeting = React.useCallback(
    () => appHistory.push(`/tote/meetings/${data?.Meeting?.MeetingId}?date=${formattedDate}`),
    [data?.Meeting?.MeetingId, formattedDate]
  )
  SetGoToNextRace(goToNextRace)
  SetGoToMeeting(goToMeeting)

  if (isFetching) {
    return (
      <div data-testid='Spinner'>
        <Spinner />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <NoticeBox
        title='Unable to place a mystery bet at this time. Please try again later.'
        noticeBoxType='Error'
      />
    )
  }

  return (
    <MainContainer data-tid-blackbook background='grey'>
      <PageHeader
        title='Mystery Bet'
        rightAlignedComponent={
          <Button
            size='sm'
            isIconOnlyButton
            color='tertiary_dark'
            disabled={isFetching}
            onClick={() => !isForSpecificRace && refetch()}
          >
            <Icon name='LineRefreshCw05' color={colors.white} />
          </Button>
        }
      />

      <MeetingInformation />
      <MysteryBetTabs />
      {isRaceClosed && !isBetConfirmed && (
        <NextRaceDrawer open onGoToMeeting={goToMeeting} onNextRace={goToNextRace} />
      )}
    </MainContainer>
  )
}

function getTopics(raceKey: string): string[] {
  return [
    getToteRaceTopic(raceKey, { type: 'statusChanged' }),
    getToteRaceTopic(raceKey, { type: 'abandonedChanged' }),
  ]
}
